import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchDetails } from "./dataAction";

const initialState = {
  data: [],
  sasUrls: [],
  searchResults: [],
  loading: false,
  error: null,
  // Categorized data with subcategories
  categories: {
    capabilities: {
      items: [],
      subcategories: [],
    },
    solutions: {
      items: [],
      subcategories: [],
    },
    functional: {
      items: [],
      subcategories: [],
    },
    client: {
      items: [],
      subcategories: [],
    },
  },

  filters: {
    category: "all",
    subcategory: "all",
    subSubcategory: "all",
  },
  filteredData: [],
};

// Helper function to categorize data and extract subcategories
// const categorizeData = (data) => {
//   const categories = {
//     capabilities: {
//       items: [],
//       subcategories: {},
//     },
//     solutions: {
//       items: [],
//       subcategories: {},
//     },
//     functional: {
//       items: [],
//       subcategories: {},
//     },
//     client: {
//       items: [],
//       subcategories: {},
//     },
//   };

//   data.forEach((item) => {
//     const path = item.path || "";
//     const pathParts = path.split("/");

//     // Skip items without a valid path
//     if (pathParts.length < 1) return;

//     // Determine category
//     let category = null;
//     if (path.startsWith("capabilities/")) {
//       category = "capabilities";
//     } else if (path.startsWith("solutions/")) {
//       category = "solutions";
//     } else if (path.startsWith("functional/")) {
//       category = "functional";
//     } else if (path.startsWith("client/")) {
//       category = "client";
//     } else {
//       return;
//     }

//     // Add to category items
//     categories[category].items.push(item);

//     // Extract and organize subcategories (second level in path)
//     if (pathParts.length >= 2) {
//       const subcategory = pathParts[1];

//       // Initialize subcategory if it doesn't exist
//       if (!categories[category].subcategories[subcategory]) {
//         categories[category].subcategories[subcategory] = {
//           items: [],
//           subSubcategories: {},
//         };
//       }

//       // Add to subcategory items
//       categories[category].subcategories[subcategory].items.push(item);

//       // Extract and organize sub-subcategories (third level in path)
//       if (pathParts.length >= 3) {
//         const subSubcategory = pathParts[2];

//         // Initialize sub-subcategory if it doesn't exist
//         if (
//           !categories[category].subcategories[subcategory].subSubcategories[
//             subSubcategory
//           ]
//         ) {
//           categories[category].subcategories[subcategory].subSubcategories[
//             subSubcategory
//           ] = {
//             items: [],
//           };
//         }

//         // Add to sub-subcategory items
//         categories[category].subcategories[subcategory].subSubcategories[
//           subSubcategory
//         ].items.push(item);
//       }
//     }
//   });

//   return categories;
// };

const categorizeData = (data) => {
  const categories = {
    capabilities: {
      items: [],
      subcategories: [], // Changed to array
    },
    solutions: {
      items: [],
      subcategories: [], // Changed to array
    },
    functional: {
      items: [],
      subcategories: [], // Changed to array
    },
    client: {
      items: [],
      subcategories: [], // Changed to array
    },
  };

  data.forEach((item) => {
    const path = item.path || "";
    const pathParts = path.split("/");

    if (pathParts.length < 1) return;

    let category = null;
    if (path.startsWith("capabilities/")) {
      category = "capabilities";
    } else if (path.startsWith("solutions/")) {
      category = "solutions";
    } else if (path.startsWith("functional/")) {
      category = "functional";
    } else if (path.startsWith("client/")) {
      category = "client";
    } else {
      return;
    }

    categories[category].items.push(item);

    if (pathParts.length >= 2) {
      const subcategoryName = pathParts[1];
      let subcategory = categories[category].subcategories.find(
        (sub) => sub.name === subcategoryName
      );

      if (!subcategory) {
        subcategory = {
          name: subcategoryName,
          items: [],
          subSubcategories: [], // Changed to array
        };
        categories[category].subcategories.push(subcategory);
      }

      subcategory.items.push(item);

      if (pathParts.length >= 3) {
        const subSubcategoryName = pathParts[2];
        let subSubcategory = subcategory.subSubcategories.find(
          (subSub) => subSub.name === subSubcategoryName
        );

        if (!subSubcategory) {
          subSubcategory = {
            name: subSubcategoryName,
            items: [],
          };
          subcategory.subSubcategories.push(subSubcategory);
        }
        subSubcategory.items.push(item);
      }
    }
  });

  return categories;
};
// Helper function to filter data based on filters
const filterData = (data, filters) => {
  const { category, subcategory, subSubcategory } = filters;

  let filtered = [...data];

  // Filter by category
  if (category !== "all") {
    filtered = filtered.filter(
      (item) => item.path && item.path.startsWith(`${category}/`)
    );

    // Filter by subcategory
    if (subcategory !== "all") {
      filtered = filtered.filter((item) => {
        const pathParts = item.path.split("/");
        return pathParts.length > 1 && pathParts[1] === subcategory;
      });

      // Filter by sub-subcategory
      if (subSubcategory !== "all") {
        filtered = filtered.filter((item) => {
          const pathParts = item.path.split("/");
          return pathParts.length > 2 && pathParts[2] === subSubcategory;
        });
      }
    }
  }

  return filtered;
};

const dataSlice = createSlice({
  name: "data",
  initialState,
  reducers: {
    // Set filter for category
    setCategoryFilter: (state, action) => {
      state.filters.category = action.payload;
      state.filters.subcategory = "all"; // Reset subcategory when changing category
      state.filters.subSubcategory = "all"; // Reset sub-subcategory
      state.filteredData = filterData(state.data, state.filters);
    },
    // Set filter for subcategory
    setSubcategoryFilter: (state, action) => {
      state.filters.subcategory = action.payload;
      state.filters.subSubcategory = "all"; // Reset sub-subcategory when changing subcategory
      state.filteredData = filterData(state.data, state.filters);
    },
    // Set filter for sub-subcategory
    setSubSubcategoryFilter: (state, action) => {
      state.filters.subSubcategory = action.payload;
      state.filteredData = filterData(state.data, state.filters);
    },
    // Reset all filters
    resetFilters: (state) => {
      state.filters = {
        category: "all",
        subcategory: "all",
        subSubcategory: "all",
      };
      state.filteredData = state.data;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.sasUrls = action.payload.sasUrls;
        state.searchResults = action.payload.searchResults;

        // Categorize the data with subcategories
        state.categories = categorizeData(action.payload.data);

        // Initialize filteredData with all data
        state.filteredData = action.payload.data;
      })
      .addCase(fetchDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// Export actions
export const {
  setCategoryFilter,
  setSubcategoryFilter,
  setSubSubcategoryFilter,
  resetFilters,
} = dataSlice.actions;

// Export the reducer
export default dataSlice.reducer;
